import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./Frontdesk.module.css";
import Contacts from "../Frontdesk/Contacts/Contacts";
import CallLogs from "../Frontdesk/CallLogs/Calllogs";
import ModulePage from "./Modulepage";
import { MODULES } from "../../../types/Catalog";
import {
  getDashboard,
  getDepartments,
  getPendingCallbacks,
  getSlots,
  type ApiOptions,
  type CallbackItem,
  type DashboardActivity,
  type DashboardAgent,
  type DashboardData,
  type DashboardDaily,
  type DashboardDepartment,
  type DashboardSlot,
  type DashboardVolume,
  type PeriodPair,
} from "./dashboardApi";
import type { DialDefaults } from "./frontdeskApi";

/* ------------------------------------------------------------------ *
 *  Front Desk – Contact Center · Call Tracking
 *  Full-screen popup modal. The rounded dashboard card is the modal;
 *  everything behind it is a dim backdrop (no white page background).
 *
 *  The dashboard is drawn on a fixed 1098 × 547 design canvas and is
 *  scaled to fit the viewport, so proportions always match the design.
 *  Every control is live: toggles, dropdowns, sliders, inputs, searches,
 *  menus, calendar, day pickers and the sidebar.
 * ------------------------------------------------------------------ */

const W = 1098;
const H = 547;

type Style = React.CSSProperties;
/** position / size helper in the local coordinate space of the parent */
const at = (left: number, top: number, width?: number, height?: number): Style => ({ left, top, width, height });
/** position helper in the shell coordinate space, using the design's pixel grid (origin = shell corner) */
const sh = (x: number, y: number, w?: number, h?: number): Style => at(x - 32, y - 88, w, h);

export type NavKey =
  | "dashboard"
  | "contacts"
  | "call-logs"
  | "calls"
  | "agents"
  | "routing"
  | "numbers"
  | "campaigns"
  | "recordings"
  | "intelligence"
  | "quality"
  | "analytics"
  | "compliance"
  | "integrations"
  | "admin";
export type MenuAction = "new-call" | "add-contact" | "export-log" | "refresh";

export interface FrontdeskProps {
  /** Called on Esc / backdrop click. When omitted the modal simply stays open. */
  onClose?: () => void;
  /** Force a fixed scale (1 = design size). Default: fit to the viewport. */
  scale?: number;
  /** Called when a sidebar item is clicked. */
  onNavigate?: (key: NavKey) => void;
  /** Called when an item of the "My Calls" menu is chosen. */
  onAction?: (action: MenuAction) => void;
  /** Gateway base URL. Default: http://localhost:54187 */
  apiBase?: string;
  /** Returns the JWT for the API. Default: reads "access_token" / "accessToken" / "token" / "jwt" from localStorage. */
  getToken?: () => string | null | undefined | Promise<string | null | undefined>;
  /** Who the signed-in user dials and listens in as (Contacts "Call", Call Logs "Make a call", "Call back", "Listen in"). All fields are optional. */
  dial?: DialDefaults;
}

type Period = "month" | "year";
type Series = "inbound" | "outbound" | "missed";

/* ---------- helpers ---------- */

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
/** Monday of the working week that contains d (Sat/Sun roll forward to the next Monday) */
const weekStart = (d: Date) => {
  const dow = d.getDay();
  return addDays(d, dow === 0 ? 1 : dow === 6 ? 2 : 1 - dow);
};

const DOW = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MONTH_SHORT = MONTH_NAMES.map((m) => m.slice(0, 3));

/** dismiss a popover on outside pointer-down */
const useDismiss = (open: boolean, close: () => void, ref: React.RefObject<HTMLElement | null>) => {
  useEffect(() => {
    if (!open) return;
    const h = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    document.addEventListener("pointerdown", h);
    return () => document.removeEventListener("pointerdown", h);
  }, [open, close, ref]);
};

/** Esc closes an open popover first, instead of closing the whole modal */
const escClose = (open: boolean, close: () => void) => (e: React.KeyboardEvent) => {
  if (open && e.key === "Escape") {
    e.stopPropagation();
    e.nativeEvent.stopPropagation();
    close();
  }
};

/* ---------- small building blocks ---------- */

const Seg: React.FC<{ style: Style; value: Period; onChange: (p: Period) => void; label: string }> = ({ style, value, onChange, label }) => (
  <span className={styles.seg} style={style} role="group" aria-label={label}>
    <button type="button" className={value === "month" ? styles.segOn : styles.segOff} aria-pressed={value === "month"} onClick={() => onChange("month")}>
      Month
    </button>
    <button type="button" className={value === "year" ? styles.segOn : styles.segOff} aria-pressed={value === "year"} onClick={() => onChange("year")}>
      Year
    </button>
  </span>
);

const Donut: React.FC<{ pct: number; style: Style }> = ({ pct, style }) => {
  const r = 16;
  const c = 2 * Math.PI * r;
  const dash = pct >= 100 ? c : Math.max(0, (c * pct) / 100 - 1);
  return (
    <svg viewBox="0 0 46 46" className={styles.donut} style={style}>
      <circle
        cx="23"
        cy="23"
        r={r}
        fill="none"
        stroke="#4884f0"
        strokeWidth="4.8"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${c}`}
        transform="rotate(-90 23 23)"
        style={{ transition: "stroke-dasharray .45s ease" }}
      />
      <text x="23" y="26" textAnchor="middle" fontSize="8.4" fontWeight="600" fill="#161d2e">
        {pct}%
      </text>
    </svg>
  );
};

const ArrowBtn: React.FC<{ style: Style; onClick: () => void; label: string }> = ({ style, onClick, label }) => (
  <button type="button" className={styles.arrowBtn} style={style} onClick={onClick} aria-label={label} title={label}>
    <svg viewBox="0 0 24 24" width="11" height="11" fill="#fff">
      <path d="M13 4.5l8 7.2-8 7.3v-4.3c-5.2 0-8.6 1.4-11.6 5 .6-6.4 4.4-10.3 11.6-11V4.5z" />
    </svg>
  </button>
);

/** the white "100" input box next to the blue arrow button */
const ValueInput: React.FC<{ style: Style; value: string; onChange: (v: string) => void; onCommit: () => void; large?: boolean; label: string }> = ({
  style,
  value,
  onChange,
  onCommit,
  large,
  label,
}) => (
  <input
    className={`${styles.valBox} ${large ? styles.valBoxLg : ""}`}
    style={style}
    value={value}
    inputMode="numeric"
    aria-label={label}
    onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
    onKeyDown={(e) => {
      if (e.key === "Enter") onCommit();
    }}
    onBlur={onCommit}
  />
);

/** stacked bars + round phone badge (replaces the coin stack in the design) */
const CallBadge: React.FC<{ tone: "blue" | "grey"; style: Style }> = ({ tone, style }) => {
  const bar = tone === "blue" ? "#6c9cf2" : "#c3c7d0";
  const dot = tone === "blue" ? "#3f7be8" : "#a9aeba";
  return (
    <svg viewBox="0 0 20 12" className={styles.badge} style={style}>
      <rect x="0" y="1.2" width="13" height="2.2" rx="1.1" fill={bar} />
      <rect x="0" y="4.9" width="13" height="2.2" rx="1.1" fill={bar} />
      <rect x="0" y="8.6" width="13" height="2.2" rx="1.1" fill={bar} />
      <circle cx="14" cy="6" r="5.4" fill={dot} />
      <g transform="translate(11.4 3.4) scale(0.22)">
        <path
          fill="#fff"
          d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
        />
      </g>
    </svg>
  );
};

const Wave: React.FC<{ color: string; style: Style }> = ({ color, style }) => (
  <svg viewBox="0 0 28 14" className={styles.wave} style={style} preserveAspectRatio="none">
    <path d="M0 9 C3 4 5 12 8 7 S13 3 16 8 S22 11 28 5" fill="none" stroke={color} strokeWidth="1" strokeLinecap="round" />
  </svg>
);

const NavIcon: React.FC<{ d: string }> = ({ d }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const MiniTable: React.FC<{
  head: string[];
  rows: string[][];
  widths: number[];
  style: Style;
  rowH: number;
  headH: number;
  empty?: string;
}> = ({ head, rows, widths, style, rowH, headH, empty }) => (
  <table className={styles.miniTable} style={style}>
    <colgroup>
      {widths.map((w, i) => (
        <col key={i} style={{ width: w }} />
      ))}
    </colgroup>
    <thead>
      <tr style={{ height: headH }}>
        <th />
        {head.map((h, i) => (
          <th key={i}>{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.length === 0 ? (
        <tr style={{ height: rowH }}>
          <td colSpan={head.length + 1} className={styles.emptyRow}>
            {empty ?? "No results"}
          </td>
        </tr>
      ) : (
        rows.map((r, i) => (
          <tr key={r[0] + i} style={{ height: rowH }}>
            <td>{i + 1}</td>
            {r.map((c, j) => (
              <td key={j}>{c}</td>
            ))}
          </tr>
        ))
      )}
    </tbody>
  </table>
);

/** small dropdown (Month / Day / Year) */
const Dropdown: React.FC<{ label: string; value: number | null; options: string[]; onSelect: (i: number | null) => void; style: Style }> = ({
  label,
  value,
  options,
  onSelect,
  style,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, ref);
  return (
    <div ref={ref} className={styles.ddWrap} style={style} onKeyDown={escClose(open, close)}>
      <button type="button" className={styles.select} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span>{value === null ? label : options[value]}</span>
        <svg viewBox="0 0 8 5" width="4" height="2.5" fill="none" stroke="#333" strokeWidth="1.2">
          <path d="M1 1l3 3 3-3" />
        </svg>
      </button>
      {open && (
        <ul className={styles.ddMenu} role="listbox" aria-label={label}>
          <li>
            <button
              type="button"
              role="option"
              aria-selected={value === null}
              className={`${styles.ddItem} ${value === null ? styles.ddItemOn : ""}`}
              onClick={() => {
                onSelect(null);
                close();
              }}
            >
              {label}
            </button>
          </li>
          {options.map((o, i) => (
            <li key={o}>
              <button
                type="button"
                role="option"
                aria-selected={value === i}
                className={`${styles.ddItem} ${value === i ? styles.ddItemOn : ""}`}
                onClick={() => {
                  onSelect(i);
                  close();
                }}
              >
                {o}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** draggable / keyboard slider */
const Slider: React.FC<{ value: number; max: number; onChange: (v: number) => void; label: string; style: Style; readOnly?: boolean; title?: string }> = ({
  value,
  max,
  onChange: onChangeRaw,
  label,
  style,
  readOnly,
  title,
}) => {
  const onChange = (v: number) => {
    if (!readOnly) onChangeRaw(v);
  };
  const ref = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState(false);
  const setFrom = (clientX: number) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    onChange(clamp((clientX - r.left) / r.width, 0, max));
  };
  return (
    <div
      ref={ref}
      className={`${styles.track} ${drag ? styles.trackDrag : ""}`}
      style={style}
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={Math.round(max * 100)}
      aria-valuenow={Math.round(value * 100)}
      title={title ?? `${label}: ${Math.round(value * 100)}%`}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        setDrag(true);
        setFrom(e.clientX);
      }}
      onPointerMove={(e) => drag && setFrom(e.clientX)}
      onPointerUp={() => setDrag(false)}
      onPointerCancel={() => setDrag(false)}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight" || e.key === "ArrowUp") {
          e.preventDefault();
          onChange(clamp(value + 0.05, 0, max));
        } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
          e.preventDefault();
          onChange(clamp(value - 0.05, 0, max));
        }
      }}
    >
      <div className={styles.trackFill} style={{ width: `${value * 100}%` }} />
      <div className={styles.trackKnob} style={{ left: `${value * 100}%` }} />
    </div>
  );
};

/* ---------- data ---------- */

type KpiKey = "inbound" | "outbound" | "answered" | "resolved";
type KpiDef = { key: KpiKey; title: string; dx: number; nx: number; bx: number; text: string };
const kpiDefs: KpiDef[] = [
  { key: "inbound", title: "Inbound", dx: 38.5, nx: 64, bx: 82, text: "Calls received on all front desk lines in the selected period, including transferred calls." },
  { key: "outbound", title: "Outbound", dx: 43.5, nx: 67.5, bx: 93, text: "Calls placed by agents to guests, vendors and for scheduled callbacks." },
  { key: "answered", title: "Answered", dx: 40.5, nx: 68.5, bx: 87, text: "Calls picked up by an agent within the agreed service level target." },
  { key: "resolved", title: "Resolved", dx: 40, nx: 68.5, bx: 93, text: "Calls closed on first contact without needing a follow-up ticket." },
];
const DEFAULT_TARGET: Record<Period, number> = { year: 100, month: 100 };

const SERIES_COLOR: Record<Series, string> = { inbound: "#1f9d8f", outbound: "#2c56d6", missed: "#e5484d" };
const SERIES_LABEL: Record<Series, string> = { inbound: "Inbound", outbound: "Outbound", missed: "Missed" };

const weekNamesFull = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const LOG_HEAD = ["Caller", "Line", "Time", "Duration", "Status", "Agent"];

const fmtDuration = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
const cap1 = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const timeText = (d: Date) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase();
/** 1, 2, 5, 10, 20, 50 ... at or above v (minimum 5): a tidy top for an axis */
const niceMax = (v: number) => {
  if (v <= 5) return 5;
  const p = 10 ** Math.floor(Math.log10(v));
  const m = v / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
};

/* lollipops: [top y (local), colour] */
const LOLLIPOPS: [number, "b" | "g"][] = [
  [34.3, "b"],
  [71, "g"],
  [34.3, "g"],
  [76, "b"],
  [59.3, "b"],
  [34.3, "b"],
  [51, "g"],
  [34.3, "b"],
  [45.3, "b"],
  [68.7, "b"],
  [52, "g"],
];

type Pt = [number, number];
const YEARS = ["2024", "2025", "2026"];
const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1));

/* candles: x, body top, body bottom, wick top, wick bottom (svg-local px), colour */
type Candle = [number, number, number, number, number, "b" | "g"];

/** one candle per hour: the body runs from the usual level (same weekday, last 8 weeks) to the actual calls */
const slotsToCandles = (slots: DashboardSlot[]): { candles: Candle[]; tips: string[] } => {
  const n = slots.length;
  if (n === 0) return { candles: [], tips: [] };
  const max = Math.max(1, ...slots.map((s) => Math.max(s.calls, s.average)));
  const yFor = (v: number) => 104 - (v / max) * 92;
  const candles: Candle[] = [];
  const tips: string[] = [];
  slots.forEach((s, i) => {
    const x = n === 1 ? 78 : 6 + (i * 144) / (n - 1);
    const a = yFor(s.average);
    const c = yFor(s.calls);
    const top = Math.min(a, c);
    const bottom = Math.max(a, c, top + 1.5);
    candles.push([x, top, bottom, clamp(top - 2, 4, 104), clamp(bottom + 2, 4, 104), s.calls >= s.average ? "b" : "g"]);
    tips.push(`${String(s.hour).padStart(2, "0")}:00 · ${s.calls} calls (usual ${Math.round(s.average * 10) / 10})`);
  });
  return { candles, tips };
};

/* ---------- charts ---------- */

const smoothPath = (pts: Pt[]) => {
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    d += ` C${p1[0] + (p2[0] - p0[0]) / 6},${p1[1] + (p2[1] - p0[1]) / 6} ${p2[0] - (p3[0] - p1[0]) / 6},${p2[1] - (p3[1] - p1[1]) / 6} ${p2[0]},${p2[1]}`;
  }
  return d;
};

const LineChart: React.FC<{ series: Series; smooth: boolean; labels: string[]; values: number[] }> = ({ series, smooth, labels, values }) => {
  /* local coordinates of the card (origin = card's top-left) */
  const vx = [27.5, 67.5, 111.5, 155, 200, 243.5, 288.5, 332.5, 377.5, 420, 465, 509];
  const hy = [45, 63, 81.5, 99.5, 117, 134.5, 152];
  const tickY = [54, 72.5, 91.5, 109.5, 128, 147.5];
  const top = 45;
  const bottom = 152;
  const n = values.length;
  const max = Math.max(1, ...values);
  const pts: Pt[] = values.map((v, i) => [n <= 1 ? 27.5 : 27.5 + (i * (504 - 27.5)) / (n - 1), bottom - (v / max) * (bottom - top)]);
  const color = SERIES_COLOR[series];
  const linePath = pts.length === 0 ? "" : smooth && pts.length > 1 ? smoothPath(pts) : `M${pts.map((p) => p.join(",")).join(" L")}`;
  const area = pts.length === 0 ? "" : `${linePath} L${pts[pts.length - 1][0]},152 L27.5,152 Z`;
  return (
    <svg viewBox="0 0 525 188" className={styles.chartSvg}>
      <defs>
        <linearGradient id="fdLineArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.52" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {hy.map((y) => (
        <line key={y} x1="27.5" x2="509" y1={y} y2={y} stroke="#c9cdd8" strokeWidth="0.7" />
      ))}
      {vx.map((x) => (
        <line key={x} x1={x} x2={x} y1="33" y2="153" stroke="#c4c9d5" strokeWidth="0.7" />
      ))}
      {tickY.map((y) => (
        <text key={y} x="11" y={y + 2.6} fontSize="7.4" fill="#6f7487">
          {Math.round((max * (bottom - y)) / (bottom - top))}
        </text>
      ))}
      {pts.map(([x], i) => (
        <text key={i} x={x} y="165" fontSize="9" fill="#767a8b" textAnchor="middle">
          {labels[i] ?? ""}
        </text>
      ))}
      {pts.length > 0 && <path d={area} fill="url(#fdLineArea)" style={{ transition: "d .35s" }} />}
      {pts.length > 0 && <path d={linePath} fill="none" stroke={color} strokeWidth="2.3" strokeLinejoin="round" />}
      {pts.map(([x, y], i) => (
        <circle key={i} className={styles.dot} cx={x} cy={y} r="4.6" fill="#fff" stroke={color} strokeWidth="2.2">
          <title>{`${SERIES_LABEL[series]} · ${labels[i] ?? ""}: ${values[i]} calls`}</title>
        </circle>
      ))}
    </svg>
  );
};

const Lollipops: React.FC<{ hours: number[]; calls: number[] }> = ({ hours, calls }) => {
  const x0 = 19.3;
  const pitch = 12.37;
  const base = 98.3;
  const height = base - 30;
  const scale = niceMax(Math.max(0, ...calls));
  const gy = [5, 4, 3, 2, 1].map((k) => base - (k / 5) * height);
  return (
    <svg viewBox="0 0 162 156" className={styles.chartSvg}>
      {gy.map((y, i) => (
        <g key={i}>
          <line x1="14" x2="152" y1={y} y2={y} stroke="#e7eaf1" strokeWidth="0.6" />
          <text x="4" y={y + 1.6} fontSize="4.6" fill="#6f7487">
            {Math.round((scale * (5 - i)) / 5)}
          </text>
        </g>
      ))}
      {calls.slice(0, LOLLIPOPS.length).map((v, i) => {
        const x = x0 + i * pitch;
        const top = base - (v / scale) * height;
        const col = LOLLIPOPS[i][1] === "b" ? "#2f7ae0" : "#2fbf5c";
        const hour = `${String(hours[i] ?? i).padStart(2, "0")}:00`;
        return (
          <g key={i} className={styles.lolly}>
            <line x1={x} x2={x} y1={top} y2={base} stroke={col} strokeWidth="3" strokeLinecap="round" />
            <circle cx={x} cy={top} r="4.7" fill="#fff" stroke={col} strokeWidth="1" />
            <circle cx={x} cy={top} r="2.1" fill={col} />
            <text x={x} y={base + 6.6} fontSize="2.6" fill="#555a6b" textAnchor="middle">
              {hour}
            </text>
            <rect x={x - 5} y={top - 5} width="10" height={base - top + 10} fill="transparent">
              <title>{`${hour} · ${v} calls`}</title>
            </rect>
          </g>
        );
      })}
    </svg>
  );
};

const Radar: React.FC<{ rows: DashboardDepartment[] }> = ({ rows }) => {
  const cx = 78.3;
  const cy = 75;
  const R = 50.7;
  const n = rows.length;
  const spokes = Math.max(3, n);
  const max = Math.max(1, ...rows.map((r) => r.calls));
  const angle = (k: number) => -Math.PI / 2 + (2 * Math.PI * k) / spokes;
  const point = (k: number, v: number): Pt => [cx + R * v * Math.cos(angle(k)), cy + R * v * Math.sin(angle(k))];
  const poly = (vals: number[]) =>
    vals
      .map((v, k) => point(k, v))
      .map((q) => `${q[0].toFixed(1)},${q[1].toFixed(1)}`)
      .join(" ");
  return (
    <svg viewBox="0 0 162 140" className={styles.chartSvg}>
      {[50.7, 37.5, 25, 12.5].map((r) => (
        <circle key={r} cx={cx} cy={cy} r={r} fill="none" stroke="#c9ceda" strokeWidth="0.6" />
      ))}
      {Array.from({ length: spokes }, (_, k) => (
        <line key={k} x1={cx} y1={cy} x2={cx + R * Math.cos(angle(k))} y2={cy + R * Math.sin(angle(k))} stroke="#d2d6e0" strokeWidth="0.5" />
      ))}
      {n >= 2 && <polygon points={poly(rows.map((r) => r.calls / max))} fill="#2f7ae0" fillOpacity="0.38" stroke="#2f7ae0" strokeWidth="1.2" strokeLinejoin="round" />}
      {n >= 2 && <polygon points={poly(rows.map((r) => r.answered / max))} fill="#35c25f" fillOpacity="0.38" stroke="#2fbf5c" strokeWidth="1.5" strokeLinejoin="round" />}
      {rows.map((r, k) => {
        const x = cx + (R + 9) * Math.cos(angle(k));
        const y = cy + (R + 7) * Math.sin(angle(k));
        return (
          <text key={`${r.name}-${k}`} x={x} y={y} fontSize="3.6" fill="#6f7487" textAnchor="middle">
            {r.name.length > 14 ? `${r.name.slice(0, 13)}…` : r.name}
            <title>{`${r.name}: ${r.calls} calls, ${r.answered} answered, ${r.missed} missed (${Math.round(r.share * 100)}%)`}</title>
          </text>
        );
      })}
      {n === 0 && (
        <text x={cx} y={cy} fontSize="4.4" fill="#6f7487" textAnchor="middle">
          No calls in this period
        </text>
      )}
    </svg>
  );
};

const Gauge: React.FC<{ level: number }> = ({ level }) => {
  /* local to Activity card: origin (750, 425) */
  const cx = 78.3;
  const cy = 171.7;
  const r = 44.3;
  const a1 = (-90 - 54) * (Math.PI / 180);
  const a2 = (-90 + 54) * (Math.PI / 180);
  const p = (a: number) => `${(cx + r * Math.cos(a)).toFixed(2)} ${(cy + r * Math.sin(a)).toFixed(2)}`;
  const deg = ((clamp(level, 0, 100) - 50) / 50) * 54;
  return (
    <svg viewBox="0 0 162 187" className={styles.chartSvg}>
      <defs>
        <radialGradient id="fdGaugeBg" cx="0.5" cy="1" r="1">
          <stop offset="0%" stopColor="#eef0f8" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#f5f6fb" stopOpacity="0" />
        </radialGradient>
      </defs>
      <path d={`M ${cx - 62} ${cy} A 62 62 0 0 1 ${cx + 62} ${cy} Z`} fill="url(#fdGaugeBg)" />
      <path d={`M ${p(a1)} A ${r} ${r} 0 0 1 ${p(a2)}`} fill="none" stroke="#2fbf5c" strokeWidth="3.2" strokeLinecap="round" />
      <polygon
        points={`${cx - 2.4},${cy} ${cx + 2.4},${cy} ${cx},${cy - 43.5}`}
        fill="#06103a"
        style={{ transform: `rotate(${deg}deg)`, transformOrigin: `${cx}px ${cy}px`, transition: "transform .6s cubic-bezier(.3,1.3,.5,1)" }}
      />
      <text x="79" y="117.5" fontSize="7.4" fontWeight="600" fill="#1b2233" textAnchor="middle">
        {level}%
      </text>
      <text x="17.3" y="172.3" fontSize="7.6" fontWeight="600" fill="#1b2233" textAnchor="middle">
        25%
      </text>
      <text x="141.7" y="171.6" fontSize="7.6" fontWeight="600" fill="#1b2233" textAnchor="middle">
        100%
      </text>
      <text x="81" y="178.2" fontSize="4.4" fill="#555a6b" textAnchor="middle">
        Service level
      </text>
    </svg>
  );
};

const Candles: React.FC<{ data: Candle[]; tips: string[] }> = ({ data, tips }) => (
  <svg viewBox="0 0 155 112" className={styles.chartSvg}>
    {[4.3, 21, 37.7, 54.3, 71, 87.7, 104.3, 121, 137.7, 154.3].map((x) => (
      <line key={x} x1={x} x2={x} y1="0" y2="110" stroke="#6d717c" strokeWidth="0.4" />
    ))}
    <line x1="2" x2="155" y1="51" y2="51" stroke="#4d5160" strokeWidth="0.45" />
    {[6, 14, 22, 30, 38, 46, 54, 62, 70, 78, 86, 94, 102].map((y) => (
      <line key={y} x1="0.5" x2="2.5" y1={y} y2={y} stroke="#9a9ea8" strokeWidth="0.5" />
    ))}
    {data.map(([x, bt, bb, wt, wb, k], i) => {
      const col = k === "b" ? "#3f7be8" : "#33c36a";
      return (
        <g key={i} className={styles.candle}>
          <line x1={x} x2={x} y1={wt} y2={wb} stroke={col} strokeWidth="0.9" />
          <rect x={x - 2} y={bt} width="4" height={Math.max(bb - bt, 2)} fill={col} />
          <rect x={x - 3} y={wt} width="6" height={wb - wt} fill="transparent">
            <title>{tips[i] ?? ""}</title>
          </rect>
        </g>
      );
    })}
    <rect x="148" y="48.5" width="7" height="5" rx="2.4" fill="#1a1d26" />
  </svg>
);

/* ---------- cards ---------- */

const KpiCard: React.FC<{ def: KpiDef; index: number; data: PeriodPair<number> | null }> = ({ def, index, data }) => {
  const [period, setPeriod] = useState<Period>("year");
  const [targets, setTargets] = useState<Record<Period, number>>(DEFAULT_TARGET);
  const [draft, setDraft] = useState(String(DEFAULT_TARGET.year));
  const target = targets[period];
  const n = data ? data[period] : 0;
  const pct = clamp(Math.round((n / target) * 100), 0, 100);

  const changePeriod = (p: Period) => {
    setPeriod(p);
    setDraft(String(targets[p]));
  };
  const commit = () => {
    const v = parseInt(draft, 10);
    if (!Number.isFinite(v) || v < 1) {
      setDraft(String(target));
      return;
    }
    setTargets((t) => ({ ...t, [period]: v }));
    setDraft(String(v));
  };

  const numCls = String(n).length >= 3 ? styles.bigNum3 : "";
  return (
    <section className={styles.card} style={sh(200 + 135 * index, 132, 118, 117)}>
      <span className={`${styles.cardTitle} ${styles.cardTitleSm}`} style={at(8, 8)}>
        {def.title}
      </span>
      <Seg style={at(54, 10)} value={period} onChange={changePeriod} label={`${def.title} period`} />
      <Donut pct={pct} style={at(def.dx - 23, 37.5, 46, 46)} />
      <div className={styles.numBlock} style={at(def.nx, 42, 112 - def.nx, 40)}>
        <span className={`${styles.bigNum} ${numCls}`}>{n}</span>
        <span className={styles.lorem}>{def.text}</span>
      </div>
      <ValueInput style={at(def.bx - 43.5, 87, 41, 17.5)} value={draft} onChange={setDraft} onCommit={commit} label={`${def.title} target`} />
      <ArrowBtn style={at(def.bx, 87, 16, 17.5)} onClick={commit} label={`Set ${def.title.toLowerCase()} target`} />
    </section>
  );
};

const CallVolumeCard: React.FC<{ data: PeriodPair<DashboardVolume> | null }> = ({ data }) => {
  const [period, setPeriod] = useState<Period>("year");
  const [series, setSeries] = useState<Series>("outbound");
  const [daily, setDaily] = useState(true);
  const vol = data ? data[period] : null;

  const radios: [number, Series][] = [
    [228, "inbound"],
    [279, "outbound"],
    [325, "missed"],
  ];
  return (
    <section className={styles.card} style={sh(200, 262, 525, 188)}>
      <LineChart series={series} smooth={!daily} labels={vol?.labels ?? []} values={vol ? vol[series] : []} />
      <span className={styles.cardTitle} style={at(8, 8)}>
        Call Volume
      </span>
      <Seg style={at(72, 11)} value={period} onChange={setPeriod} label="Call volume period" />
      <div role="radiogroup" aria-label="Call direction" className={styles.radioGroup}>
        {radios.map(([cx, key]) => (
          <React.Fragment key={key}>
            <button
              type="button"
              role="radio"
              aria-checked={series === key}
              aria-label={SERIES_LABEL[key]}
              className={`${styles.radio} ${series === key ? styles.radioOn : ""}`}
              style={at(cx - 4.5, 8.5, 9, 9)}
              onClick={() => setSeries(key)}
            />
            <button type="button" tabIndex={-1} className={styles.radioText} style={at(cx + 8, 9)} onClick={() => setSeries(key)}>
              {SERIES_LABEL[key]}
            </button>
          </React.Fragment>
        ))}
      </div>
      <span className={styles.toggleLabel} style={at(444, 6)}>
        Daily view
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={daily}
        aria-label="Daily view"
        className={`${styles.switch} ${daily ? styles.switchOn : ""}`}
        style={at(497, 9, 15, 8)}
        onClick={() => setDaily((d) => !d)}
      >
        <span className={styles.switchKnob} />
      </button>
    </section>
  );
};

const AgentsCard: React.FC<{ online: number; data: PeriodPair<DashboardAgent[]> | null }> = ({ online, data }) => {
  const [period, setPeriod] = useState<Period>("year");
  const [cap, setCap] = useState(100);
  const [draft, setDraft] = useState("100");
  const rows = data ? data[period] : [];

  const commit = () => {
    const v = parseInt(draft, 10);
    if (!Number.isFinite(v)) {
      setDraft(String(cap));
      return;
    }
    const c = clamp(v, 10, 100);
    setCap(c);
    setDraft(String(c));
  };
  const ys = [41.5, 54.5, 68, 81, 94, 107.5, 120.5, 133.5];
  return (
    <section className={styles.card} style={sh(200, 466, 255, 146)}>
      <span className={styles.cardTitle} style={at(8, 8)}>
        Agents
      </span>
      <Seg style={at(58, 11)} value={period} onChange={setPeriod} label="Agents period" />
      <div className={`${styles.numBlock} ${styles.numBlockWide}`} style={at(14, 41, 54, 46)}>
        <span className={styles.bigNum}>{String(online).padStart(2, "0")}</span>
        <span className={styles.lorem}>Agents online and taking calls on the front desk queue right now.</span>
      </div>
      <ValueInput style={at(11.5, 96.5, 51, 21.5)} value={draft} onChange={setDraft} onCommit={commit} large label="Utilisation cap (%)" />
      <ArrowBtn style={at(65, 96.5, 21.5, 21.5)} onClick={commit} label="Apply utilisation cap" />
      {ys.map((y, i) => (
        <Slider
          key={i}
          readOnly
          value={Math.min(rows[i]?.share ?? 0, cap / 100)}
          max={cap / 100}
          label={rows[i]?.name ?? "No data"}
          title={rows[i] ? `${rows[i].name}: ${rows[i].calls} answered calls` : "No data"}
          style={at(104.5, y - 3.75, 139, 7.5)}
          onChange={() => undefined}
        />
      ))}
    </section>
  );
};

const CallLogCard: React.FC<{ query: string; data: DashboardData | null }> = ({ query, data }) => {
  const [period, setPeriod] = useState<Period>("year");
  const q = query.trim().toLowerCase();
  const rows = useMemo(() => {
    const all = (data ? data.callLog[period] : []).map((r) => [r.caller, r.line, r.localTime, fmtDuration(r.durationSeconds), r.status, r.agent ?? "—"]);
    const hit = q ? all.filter((r) => r.join(" ").toLowerCase().includes(q)) : all;
    return hit.slice(0, 3);
  }, [data, period, q]);
  const tubes = data ? data.callsByHour[period] : [];
  const tubeMax = Math.max(1, ...tubes);
  return (
    <section className={styles.card} style={sh(478, 466, 250, 146)}>
      <span className={styles.cardTitle} style={at(11, 8)}>
        Call Log
      </span>
      <Seg style={at(57, 11)} value={period} onChange={setPeriod} label="Call log period" />
      <MiniTable
        style={at(13.5, 29, 236)}
        widths={[15, 37, 28.5, 28.5, 41.5, 41.5, 43.5]}
        headH={9}
        rowH={10}
        head={LOG_HEAD}
        rows={rows}
        empty={q ? `No calls match "${query.trim()}"` : "No calls yet"}
      />
      <div className={styles.rulers} style={at(14.5, 75, 233.5, 61.5)} />
      {Array.from({ length: 10 }, (_, i) => tubes[i] ?? 0).map((v, i) => (
        <div key={i} className={styles.tube} style={at(28 + i * 23.2 - 7.5, 75, 15, 61.5)} title={`${i + 8}:00 · ${v} calls`}>
          <div className={styles.tubeFill} style={{ height: `${(v / tubeMax) * 100}%` }} />
        </div>
      ))}
    </section>
  );
};

const DailyCard: React.FC<{ data: DashboardDaily | null }> = ({ data }) => (
  <section className={styles.card} style={sh(750, 129, 162, 156)}>
    <Lollipops hours={data?.hours ?? []} calls={data?.calls ?? []} />
    <span className={styles.smallTitle} style={at(8, 8)}>
      Daily Calls
    </span>
    <MiniTable
      style={at(7, 110, 140)}
      widths={[7, 38, 26, 22, 47]}
      headH={8}
      rowH={8.3}
      head={["Direction", "Line", "Time", "Duration"]}
      rows={(data?.recent ?? []).map((r) => [cap1(r.direction), r.line, r.localTime, fmtDuration(r.durationSeconds)])}
      empty="No calls today"
    />
  </section>
);

const DeptCard: React.FC<{ api: ApiOptions; reload: number }> = ({ api, reload }) => {
  const [m, setM] = useState<number | null>(null);
  const [d, setD] = useState<number | null>(null);
  const [y, setY] = useState<number | null>(null);
  const [rows, setRows] = useState<DashboardDepartment[]>([]);

  useEffect(() => {
    const ctl = new AbortController();
    getDepartments(
      api,
      { year: y === null ? undefined : Number(YEARS[y]), month: m === null ? undefined : m + 1, day: d === null ? undefined : d + 1 },
      ctl.signal,
    )
      .then((r) => setRows(r.departments))
      .catch(() => {
        if (!ctl.signal.aborted) setRows([]);
      });
    return () => ctl.abort();
  }, [api, m, d, y, reload]);

  return (
    <section className={styles.card} style={sh(750, 285, 162, 140)}>
      <Radar rows={rows} />
      <span className={styles.smallTitle} style={at(8, 2)}>
        Calls by Dept
      </span>
      <span className={styles.subTitle} style={at(8, 11)}>
        Share of calls per department
      </span>
      <Dropdown label="Month" value={m} options={MONTH_SHORT} onSelect={setM} style={at(80, 3, 22.3, 9)} />
      <Dropdown label="Day" value={d} options={DAYS} onSelect={setD} style={at(105, 3, 22.3, 9)} />
      <Dropdown label="Year" value={y} options={YEARS} onSelect={setY} style={at(130.7, 3, 21.6, 9)} />
    </section>
  );
};

const ActivityCard: React.FC<{ data: PeriodPair<DashboardActivity> | null }> = ({ data }) => {
  const [period, setPeriod] = useState<Period>("year");
  const a = data ? data[period] : null;
  const level = a?.serviceLevelPercent ?? 0;
  return (
    <section className={styles.card} style={sh(750, 425, 162, 187)}>
      <Gauge level={level} />
      <span className={styles.smallTitle} style={at(3, 2)}>
        Activity
      </span>
      <Seg style={at(99, 5)} value={period} onChange={setPeriod} label="Activity period" />
      <div className={styles.money} style={at(1.7, 31, 146, 27)} />
      <CallBadge tone="blue" style={at(16, 40, 20, 12)} />
      <span className={styles.moneyVal} style={at(39.3, 35)}>
        {`${a?.talkMinutes ?? 0} min`}
      </span>
      <span className={styles.moneyBox} style={at(108, 36, 28.7, 19)}>
        <Wave color="#b8d2fa" style={at(4, 4, 21, 11)} />
      </span>
      <div className={styles.money} style={at(1.7, 66.7, 146, 27.6)} />
      <CallBadge tone="blue" style={at(16, 75, 20, 12)} />
      <span className={styles.moneyVal} style={at(39.3, 70.5)}>
        {`${a?.calls ?? 0} calls`}
      </span>
      <span className={`${styles.moneyBox} ${styles.moneyBoxGreen}`} style={at(108, 71.7, 28.7, 19.3)}>
        <Wave color="#b5ecc5" style={at(4, 4, 21, 11)} />
      </span>
    </section>
  );
};

type CallbackEvent = { date: Date; t1: string; t2: string; a: string; b: string; s1: string; s2: string };

const MENU_ITEMS: [MenuAction, string, string][] = [
  ["new-call", "New call", "Starting a new call…"],
  ["add-contact", "Add contact", "Add contact form opened"],
  ["export-log", "Export call log", "Call log exported (call_log.csv)"],
  ["refresh", "Refresh data", "Data refreshed"],
];

const AccountCard: React.FC<{
  now: Date;
  tick: number;
  api: ApiOptions;
  missedLastHour: number;
  agentsOnline: number;
  onAction: (a: MenuAction, message: string) => void;
}> = ({ now, tick, api, missedLastHour, agentsOnline, onAction }) => {
  const base = useMemo(() => weekStart(now), [now]);
  const [selected, setSelected] = useState<Date>(base);
  const [view, setView] = useState({ y: base.getFullYear(), m: base.getMonth() });
  const [query, setQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [seen, setSeen] = useState(false);
  const [dayIdx, setDayIdx] = useState((new Date().getDay() + 6) % 7);
  const [touched, setTouched] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const closeBell = useCallback(() => setBellOpen(false), []);
  useDismiss(menuOpen, closeMenu, menuRef);
  useDismiss(bellOpen, closeBell, bellRef);

  const [callbacks, setCallbacks] = useState<CallbackItem[]>([]);
  useEffect(() => {
    const ctl = new AbortController();
    getPendingCallbacks(api, ctl.signal)
      .then(setCallbacks)
      .catch(() => {
        if (!ctl.signal.aborted) setCallbacks([]);
      });
    return () => ctl.abort();
  }, [api, tick]);

  const events = useMemo<CallbackEvent[]>(
    () =>
      callbacks
        .map((c) => {
          const when = new Date(c.scheduledFor ?? c.requestedAt);
          return {
            date: when,
            t1: timeText(when),
            t2: timeText(new Date(c.requestedAt)),
            a: `Call back ${c.number}`,
            b: `Status: ${c.status}`,
            s1: c.note ?? "Callback requested by the caller.",
            s2: `${c.attemptCount} attempt${c.attemptCount === 1 ? "" : "s"} so far`,
          };
        })
        .sort((x, y) => x.date.getTime() - y.date.getTime()),
    [callbacks],
  );

  const [slots, setSlots] = useState<DashboardSlot[]>([]);
  const slotDate = useMemo(() => ymd(addDays(now, dayIdx - ((now.getDay() + 6) % 7))), [now, dayIdx]);
  useEffect(() => {
    const ctl = new AbortController();
    getSlots(api, slotDate, ctl.signal)
      .then((r) => setSlots(r.slots))
      .catch(() => {
        if (!ctl.signal.aborted) setSlots([]);
      });
    return () => ctl.abort();
  }, [api, slotDate, tick]);
  const { candles, tips } = useMemo(() => slotsToCandles(slots), [slots]);

  const q = query.trim().toLowerCase();
  const visible = useMemo(() => {
    const hit = events.filter((e) => !q || `${e.a} ${e.b} ${e.s1} ${e.s2}`.toLowerCase().includes(q));
    const onDay = touched ? hit.filter((e) => sameDay(e.date, selected)) : [];
    const rest = hit.filter((e) => !touched || !sameDay(e.date, selected));
    return [...onDay, ...rest].slice(0, 2);
  }, [events, q, selected, touched]);

  /* calendar grid for the viewed month */
  const cal = useMemo(() => {
    const lead = new Date(view.y, view.m, 1).getDay();
    const count = new Date(view.y, view.m + 1, 0).getDate();
    const cells: (number | null)[] = [...Array(lead).fill(null), ...Array.from({ length: count }, (_, i) => i + 1)];
    while (cells.length % 7) cells.push(null);
    const rows: (number | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [view]);
  const rowH = 96.5 / cal.length;
  const isMarked = (d: Date) => sameDay(d, now) || sameDay(d, addDays(now, 4));
  const hasEvent = (d: Date) => events.some((e) => sameDay(e.date, d));

  const pick = (d: Date) => {
    setTouched(true);
    setSelected(d);
    setView({ y: d.getFullYear(), m: d.getMonth() });
  };
  const shiftMonth = (delta: number) => {
    const d = new Date(view.y, view.m + delta, 1);
    setView({ y: d.getFullYear(), m: d.getMonth() });
  };

  const week = Array.from({ length: 5 }, (_, i) => addDays(weekStart(selected), i));
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const weekNames = weekNamesFull;

  return (
    <section className={styles.card} style={sh(933, 132, 167, 482)}>
      <div ref={bellRef} className={styles.popAnchor} style={at(8, 7.5, 11, 11)} onKeyDown={escClose(bellOpen, closeBell)}>
        <button
          type="button"
          className={styles.bellBtn}
          aria-label="Notifications"
          aria-expanded={bellOpen}
          onClick={() => {
            setBellOpen((o) => !o);
            setSeen(true);
          }}
        >
          <svg viewBox="0 0 24 24" className={styles.bell}>
            <path fill="#1d2b55" d="M12 3a6 6 0 00-6 6v4l-2 3h16l-2-3V9a6 6 0 00-6-6zM10 19a2 2 0 004 0z" />
            {!seen && <circle cx="19" cy="5" r="3.6" fill="#e0202c" />}
          </svg>
        </button>
        {bellOpen && (
          <div className={styles.pop} style={{ left: 0, top: 15, width: 128 }} role="menu">
            <div className={styles.popHead}>Notifications</div>
            <div className={styles.popNote}>{`${missedLastHour} missed call${missedLastHour === 1 ? "" : "s"} in the last hour`}</div>
            <div className={styles.popNote}>{`${events.length} callback${events.length === 1 ? "" : "s"} waiting`}</div>
            <div className={styles.popNote}>{`${agentsOnline} agent${agentsOnline === 1 ? "" : "s"} online`}</div>
          </div>
        )}
      </div>
      <span className={styles.accTitle} style={at(0, 7, 167)}>
        My Calls
      </span>
      <div ref={menuRef} className={styles.popAnchor} style={at(150, 7, 9, 9)} onKeyDown={escClose(menuOpen, closeMenu)}>
        <button type="button" className={styles.burger} aria-label="Menu" aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
          <i />
          <i />
          <i />
        </button>
        {menuOpen && (
          <div className={styles.pop} style={{ right: -3, top: 14, width: 96 }} role="menu">
            {MENU_ITEMS.map(([key, label, msg]) => (
              <button
                key={key}
                type="button"
                role="menuitem"
                className={styles.popItem}
                onClick={() => {
                  closeMenu();
                  onAction(key, msg);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
      <label className={styles.accSearch} style={at(38, 32, 91, 11)}>
        <input
          className={styles.accInput}
          value={query}
          placeholder="Search callbacks"
          aria-label="Search callbacks"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape" && query) {
              e.stopPropagation();
              e.nativeEvent.stopPropagation();
              setQuery("");
            }
          }}
        />
        <svg viewBox="0 0 24 24" width="7" height="7" fill="none" stroke="#a5acba" strokeWidth="2.6" strokeLinecap="round">
          <circle cx="10" cy="10" r="6" />
          <path d="M15 15l5 5" />
        </svg>
      </label>

      <button type="button" className={styles.calNav} style={at(5, 47)} aria-label="Previous month" onClick={() => shiftMonth(-1)}>
        ‹
      </button>
      <span className={styles.calYear} style={at(18, 48)}>
        {view.y}/{String(view.m + 1).padStart(2, "0")}
      </span>
      <span className={styles.calYear} style={{ right: 18, top: 48 }}>
        {MONTH_NAMES[view.m]}
      </span>
      <button type="button" className={styles.calNav} style={{ right: 4, top: 47 }} aria-label="Next month" onClick={() => shiftMonth(1)}>
        ›
      </button>
      {DOW.map((d, i) => (
        <span key={d} className={styles.calDow} style={at(16.3 + i * 19.1, 70, 19.1)}>
          {d}
        </span>
      ))}
      {cal.map((row, ri) =>
        row.map((d, ci) => {
          if (d === null) return null;
          const date = new Date(view.y, view.m, d);
          const cls = [styles.calCell, isMarked(date) ? styles.calMarked : "", touched && sameDay(date, selected) ? styles.calSelected : ""].join(" ");
          return (
            <button
              key={`${ri}-${ci}`}
              type="button"
              className={cls}
              style={at(16.3 + ci * 19.1, 76.3 + ri * rowH, 19.1, rowH)}
              aria-label={date.toDateString()}
              aria-pressed={sameDay(date, selected)}
              onClick={() => pick(date)}
            >
              {d}
            </button>
          );
        }),
      )}
      {week.map((d, i) => {
        const on = sameDay(d, selected);
        const green = !on && hasEvent(d);
        return (
          <button
            key={i}
            type="button"
            className={`${styles.pill} ${on ? styles.pillBlue : green ? styles.pillGreen : ""}`}
            style={at(8 + i * 32, 184.7, 23.3, 43)}
            aria-pressed={on}
            aria-label={d.toDateString()}
            onClick={() => pick(d)}
          >
            <b>{dayNames[i]}</b>
            <em>{d.getDate()}</em>
          </button>
        );
      })}
      {visible.length === 0 && (
        <div className={styles.event} style={at(3.7, 242.3, 156.3, 40.7)}>
          <span className={styles.evEmpty}>No callbacks match “{query.trim()}”</span>
        </div>
      )}
      {visible.map((ev, i) => (
        <div
          key={ev.date.toISOString() + ev.a}
          className={`${styles.event} ${styles.eventBtn}`}
          style={at(3.7, i === 0 ? 242.3 : 293.7, 156.3, 40.7)}
          role="button"
          tabIndex={0}
          title="Show this day"
          onClick={() => pick(ev.date)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              pick(ev.date);
            }
          }}
        >
          <span className={`${styles.evTile} ${i === 0 ? styles.pillGreen : styles.pillBlue}`}>
            <b>{DOW[ev.date.getDay()]}</b>
            <em>{ev.date.getDate()}</em>
            <Wave color="rgba(255,255,255,0.55)" style={at(0, 27, 21, 11)} />
          </span>
          <span className={styles.evTime} style={at(25, 10)}>
            {ev.t1}
          </span>
          <span className={styles.evTime} style={at(25, 22.2)}>
            {ev.t2}
          </span>
          <span className={styles.evHead} style={at(55, 4)}>
            {`Due ${MONTH_SHORT[ev.date.getMonth()]} ${ev.date.getDate()}`}
          </span>
          <span className={styles.evMain} style={at(56, 9)}>
            {ev.a}
          </span>
          <span className={styles.evSub} style={at(56, 17, 70)}>
            {ev.s1}
          </span>
          <span className={styles.evMain} style={at(56, 22.5)}>
            {ev.b}
          </span>
          <span className={styles.evSub} style={at(56, 30.5, 70)}>
            {ev.s2}
          </span>
          <CallBadge tone="grey" style={at(128, 17, 22, 13)} />
        </div>
      ))}
      <div className={styles.candles} style={at(7, 348, 155, 112)}>
        <Candles data={candles} tips={tips} />
      </div>
      <div className={styles.dayBar} style={at(8.7, 464.7, 152.6, 10)} role="group" aria-label="Day of week">
        {weekNames.map((d, i) => (
          <button key={d} type="button" className={i === dayIdx ? styles.dayOn : undefined} aria-pressed={i === dayIdx} onClick={() => setDayIdx(i)}>
            {d}
          </button>
        ))}
      </div>
    </section>
  );
};

/* ---------- main component ---------- */

const useFitScale = (override?: number) => {
  const [s, setS] = useState(1);
  useEffect(() => {
    if (override) {
      setS(override);
      return;
    }
    const fit = () => setS(Math.max(0.3, Math.min((window.innerWidth - 32) / W, (window.innerHeight - 32) / H, 2.5)));
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [override]);
  return s;
};

const ICON_DASHBOARD = "M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z";
const ICON_CONTACTS = "M12 12a4 4 0 100-8 4 4 0 000 8zM4 21c0-4 4-6 8-6s8 2 8 6";
const ICON_CALL_LOGS =
  "M6.6 10.8a15 15 0 006.6 6.6l2.2-2.2a1 1 0 011-.25 11.4 11.4 0 003.6.6 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.6 3.6a1 1 0 01-.25 1z";

/** Front desk pages first, then one page per backend module (generated from the API catalog). */
const NAV: [NavKey, string, string][] = [
  ["dashboard", "Dashboard", ICON_DASHBOARD],
  ["contacts", "Contacts", ICON_CONTACTS],
  ["call-logs", "Call Logs", ICON_CALL_LOGS],
  ...MODULES.map((m): [NavKey, string, string] => [m.key as NavKey, m.label, m.icon]),
];
const NAV_LABEL: Record<string, string> = Object.fromEntries(NAV.map(([k, l]) => [k, l]));

const Frontdesk: React.FC<FrontdeskProps> = ({ onClose, scale, onNavigate, onAction, apiBase, getToken, dial }) => {
  const s = useFitScale(scale);
  const [nav, setNav] = useState<NavKey>("dashboard");
  /** module pages are mounted on first visit and then kept, so their state survives tab changes */
  const [visited, setVisited] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [query, setQuery] = useState("");
  const [tick, setTick] = useState(0);
  const [toast, setToast] = useState<{ msg: string; id: number } | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  /* API access: one stable options object, so a new getToken closure on every parent render never triggers a reload */
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  const api = useMemo<ApiOptions>(() => ({ apiBase, getToken: () => tokenRef.current?.() }), [apiBase]);
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    let ctl = new AbortController();
    const load = () => {
      ctl.abort();
      ctl = new AbortController();
      const mine = ctl;
      getDashboard(api, mine.signal)
        .then((d) => {
          if (!alive) return;
          setData(d);
          setError(null);
        })
        .catch((e) => {
          if (alive && !mine.signal.aborted) setError(e instanceof Error ? e.message : "Could not load the dashboard.");
        });
    };
    load();
    const timer = window.setInterval(load, 60000);
    return () => {
      alive = false;
      ctl.abort();
      window.clearInterval(timer);
    };
  }, [api, tick]);

  const missedLastHour = useMemo(() => {
    if (!data) return 0;
    const since = Date.now() - 3600000;
    return [...data.daily.recent, ...data.callLog.month].filter((r) => (r.status === "Missed" || r.status === "Abandoned") && new Date(r.startedAt).getTime() >= since).length;
  }, [data]);

  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const now = useMemo(() => new Date(), []);
  const headerDate = `${MONTH_NAMES[now.getMonth()]} ${now.getDate()}, ${DOW[now.getDay()]}`;

  const searchLabel = nav === "dashboard" ? "Search calls" : `Search ${(NAV_LABEL[nav] ?? "").toLowerCase()}`;

  const showToast = (msg: string) => {
    window.clearTimeout(toastTimer.current);
    setToast({ msg, id: Date.now() });
    toastTimer.current = window.setTimeout(() => setToast(null), 2200);
  };
  const handleAction = (a: MenuAction, msg: string) => {
    if (a === "refresh") setTick((t) => t + 1);
    showToast(msg);
    onAction?.(a);
  };

  return (
   <div className={styles.frame} style={{ width: W * s, height: H * s }}>
        <div className={styles.shell} style={{ width: W, height: H, transform: `scale(${s})` }}>
          {/* ───────── sidebar ───────── */}
          <aside className={styles.sidebar}>
            <div className={styles.avatar}>
              {missedLastHour > 0 && (
                <span className={styles.avatarBadge} title={`${missedLastHour} missed in the last hour`}>
                  {missedLastHour > 99 ? "99+" : missedLastHour}
                </span>
              )}
            </div>
            <nav
              aria-label="Main"
              style={{ position: "absolute", left: 0, right: 0, top: 108, bottom: 8, overflowY: "auto", overflowX: "hidden", scrollbarWidth: "thin" }}
            >
              {NAV.map(([key, label, icon]) => (
                <button
                  key={key}
                  type="button"
                  className={`${styles.navItem} ${nav === key ? styles.navActive : ""}`}
                  style={{ position: "relative", left: 0, top: 0, width: "100%", height: 30, margin: "0 0 2px" }}
                  aria-current={nav === key ? "page" : undefined}
                  onClick={() => {
                    setNav(key);
                    setQuery("");
                    setVisited((v) => (v.has(key) ? v : new Set(v).add(key)));
                    onNavigate?.(key);
                  }}
                >
                  <NavIcon d={icon} />
                  {label}
                </button>
              ))}
            </nav>
          </aside>

          {/* ───────── header ───────── */}
          <svg viewBox="0 0 24 24" className={styles.gridIcon} fill="none" stroke="#33405f" strokeWidth="1.7">
            <rect x="3" y="3" width="7.5" height="7.5" />
            <rect x="13.5" y="3" width="7.5" height="7.5" />
            <rect x="3" y="13.5" width="7.5" height="7.5" />
            <rect x="13.5" y="13.5" width="7.5" height="7.5" />
          </svg>
          <div className={styles.title} style={sh(231, 98)}>
            {nav === "dashboard" ? "Call Tracker" : (NAV_LABEL[nav] ?? "Call Tracker")}
          </div>
          <div className={styles.date} style={sh(231, 112)}>
            {headerDate}
          </div>
          <label className={styles.search} style={sh(321, 99, 134, 18)}>
            <input
              className={styles.searchInput}
              value={query}
              placeholder={searchLabel}
              aria-label={searchLabel}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape" && query) {
                  e.stopPropagation();
                  e.nativeEvent.stopPropagation();
                  setQuery("");
                }
              }}
            />
            {query ? (
              <button type="button" className={styles.clearBtn} aria-label="Clear search" onClick={() => setQuery("")}>
                ×
              </button>
            ) : (
              <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="#9aa3b5" strokeWidth="2.6" strokeLinecap="round">
                <circle cx="10" cy="10" r="6" />
                <path d="M15 15l5 5" />
              </svg>
            )}
          </label>

          {/* ───────── contacts tab (kept mounted so its state survives tab changes) ───────── */}
          <div style={{ position: "absolute", ...at(168, 44, 900, 485), display: nav === "contacts" ? "block" : "none" }}>
            <Contacts query={nav === "contacts" ? query : ""} api={api} {...(dial ? { dial } : {})} />
          </div>

          {/* ───────── cards (dashboard) ───────── */}
          {/* call logs tab */}
          <div style={{ position: "absolute", ...at(168, 44, 900, 485), display: nav === "call-logs" ? "block" : "none" }}>
            <CallLogs query={nav === "call-logs" ? query : ""} api={api} {...(dial ? { dial } : {})} />
          </div>

          {/* one page per backend module */}
          {MODULES.map((m) =>
            visited.has(m.key) ? (
              <div key={m.key} style={{ position: "absolute", ...at(168, 44, 900, 485), display: nav === m.key ? "block" : "none" }}>
                <ModulePage module={m} api={api} query={nav === m.key ? query : ""} />
              </div>
            ) : null,
          )}

          {/* cards (dashboard) */}
          <div style={{ display: nav === "dashboard" ? "contents" : "none" }}>
          {kpiDefs.map((def, i) => (
            <KpiCard
              key={def.title}
              def={def}
              index={i}
              data={data ? { month: data.kpis.month[def.key], year: data.kpis.year[def.key] } : null}
            />
          ))}
          <CallVolumeCard data={data?.volume ?? null} />
          <AgentsCard online={data?.agentsOnline ?? 0} data={data?.agents ?? null} />
          <CallLogCard query={query} data={data} />
          <DailyCard data={data?.daily ?? null} />
          <DeptCard api={api} reload={tick} />
          <ActivityCard data={data?.activity ?? null} />
          <AccountCard now={now} tick={tick} api={api} missedLastHour={missedLastHour} agentsOnline={data?.agentsOnline ?? 0} onAction={handleAction} />
          </div>

          {toast && (
            <div key={toast.id} className={styles.toast} role="status">
              {toast.msg}
            </div>
          )}
          {error && !toast && (
            <div className={styles.toast} role="alert">
              {error}
            </div>
          )}
        </div>
      </div>
  );
};

export default Frontdesk;